/* Presentation rules for the capability matrix. Pure — no React, no fetch.
 *
 * **The matrix is a gate, not a report, and the screen has to read as one.** `core.ai.evals`' brief is
 * explicit: the output "gates which features are enabled for which serving profile", so this is
 * consulted before a feature runs rather than being a page somebody reads. Two consequences shape every
 * function here.
 *
 * **The asymmetry is deliberate and must be visible: absence of evidence permits, evidence of failure
 * blocks.** `unproven` is allowed — an unevaluated model is not a blocked one, because failing closed
 * would have disabled every AI feature on every deployment the day the gate shipped, which is a gate
 * somebody switches off. Only `failed` withholds. `insufficient_evidence` is its own verdict because a
 * model that passed 4 of 4 has not been *shown* capable, and calling that proven is how a capability
 * matrix becomes a rubber stamp.
 *
 * **The verdict is derived, never stored.** A stored verdict would freeze against whatever bar was in
 * force that day, so raising a bar later would leave rows asserting a conclusion the platform no longer
 * draws. That is why every row carries its counts and its `suite_version`, and why the screen shows
 * them next to the verdict rather than instead of it.
 */

import type { BadgeTone } from '@/shared/ui/StatusBadge';

import type { Capability, CapabilityResult, CapabilityVerdict } from '../api/evals';

/** Verdict → tone. Four verdicts, four appearances, and only one of them is red.
 *
 * `failed` is `danger` because it is the only verdict that withholds a feature. `unproven` is `neutral`
 * rather than `warn`: an unevaluated model is the *normal* state of a fresh deployment and amber on
 * every row would train somebody to ignore the column. `insufficient_evidence` is `unknown` — the hue
 * this design system reserves for "we could not tell" — because a model that passed everything it was
 * asked is not failing and is not proven either, and it must not borrow a point on a severity scale.
 */
const VERDICT_TONES = {
  proven: 'ok',
  failed: 'danger',
  insufficient_evidence: 'unknown',
  unproven: 'neutral',
} as const satisfies Record<CapabilityVerdict, BadgeTone>;

export function verdictTone(verdict: CapabilityVerdict): BadgeTone {
  /* **A total map, and the `switch` it replaces had the worst possible default.** `default: 'neutral'`
     is the *permitted* tone — the one `unproven` wears — so a fifth verdict, or a renamed one arriving
     from a server one version ahead of this bundle, rendered as benign. A `failed` row would have lost
     its red badge. `satisfies Record<CapabilityVerdict, BadgeTone>` makes an omission a compile error
     and restores the exhaustiveness a `default:` clause discards.

     Exactly the hole closed in `core.ai.anomaly`'s presentation model one commit earlier and left open
     here, which is the argument for the map being the house form rather than a judgement per file. */
  return VERDICT_TONES[verdict];
}

/** The i18n key naming a verdict in words. `insufficient_evidence` is the one that must not read as a
 * failure, and `unproven` the one that must not read as a pass. */
export function verdictKey(verdict: CapabilityVerdict): string {
  return `evals.verdict.${verdict}`;
}

export function capabilityKey(capability: Capability): string {
  return `evals.capability.${capability}`;
}

/** A pass rate as a percentage string, or `null` when nothing was counted.
 *
 * **`null` rather than 0%, and the contract says why: "never 0.0, which would read as failed
 * everything".** A run whose every case was `unavailable` — a model nobody could reach — counted
 * nothing, and rendering 0% would attribute an outage to the model's ability. That is precisely the
 * defect `CaseVerdict.UNAVAILABLE` exists to prevent, and it is worth restating here because a screen
 * is where a null most easily becomes a zero.
 */
function passRateOf(result: CapabilityResult): string | null {
  if (result.pass_rate == null) return null;
  return `${(result.pass_rate * 100).toFixed(0)}%`;
}

/** How many cases were excluded from the bar because the model could not be reached.
 *
 * Surfaced separately from `failed` because they are the distinction that made a real defect: a
 * provider returning HTTP 503 was once scored as *incapable*, so twelve outages became twelve failures
 * and a FAILED verdict for a model nobody had reached. `counted` excludes them; this is the number that
 * says how much of the run was actually a measurement.
 */
export function unavailableOf(result: CapabilityResult): number {
  return result.unavailable;
}

/** One row per (model, capability) pair, in the reference's order.
 *
 * **A flat list rather than a grid, and that is the reference's structure.** The screen used to be a
 * matrix — models down, capabilities across — which is the denser shape and the wrong one for the
 * question a reader arrives with. The reference gives each pair its own row with an *Evidence*
 * column, so the counts behind a verdict sit beside it in prose rather than crammed into a cell.
 * That is what makes this evidence rather than a declaration, which is the module's whole claim.
 *
 * Models are sorted by name and capabilities kept in a fixed order, so rows do not reshuffle between
 * refreshes. **A capability with no result is still a row**, carrying `null`: "never evaluated" is a
 * fact worth a line, and the gate permits it for a different reason than it permits a recorded but
 * inconclusive run. Omitting those rows would make a fresh model look like it had no capabilities
 * rather than no evidence.
 */
export function capabilityRows(
  results: readonly CapabilityResult[],
  capabilities: readonly Capability[],
): readonly {
  readonly model: string;
  readonly capability: Capability;
  readonly result: CapabilityResult | null;
}[] {
  const models = [...new Set(results.map((result) => result.model))].sort((left, right) =>
    left.localeCompare(right),
  );
  return models.flatMap((model) =>
    capabilities.map((capability) => ({
      model,
      capability,
      result:
        results.find(
          (result) => result.model === model && result.capability === capability,
        ) ?? null,
    })),
  );
}

/** The verdict to present for a pair, where an absent result is `unproven`.
 *
 * The API never sends `unproven` — a stored row always has counts, so its derived verdict is one of
 * the other three. `unproven` is what the *gate* answers for a model it has never seen, and on this
 * screen it is what an empty row means. Deriving it here keeps the screen from having to special-case
 * a null twice, once for the badge and once for the evidence sentence.
 */
export function verdictFor(result: CapabilityResult | null): CapabilityVerdict {
  return result === null ? 'unproven' : result.verdict;
}

/** The Evidence cell: which sentence to render, and the numbers it needs.
 *
 * One sentence per verdict, because the useful thing to say differs completely. A passed row states
 * the arithmetic; a failed row states the arithmetic *and* the bar it missed, since the bar is what
 * makes the verdict actionable; a thin row states how far short of conclusive it is; a never-run row
 * states that nothing was measured. The reference writes all four out, and this returns the key plus
 * its values rather than a formatted string so the copy stays in the catalogue.
 */
export function evidenceFor(
  result: CapabilityResult | null,
  minimumCases: number,
  minimumPassRate: number,
): { readonly key: string; readonly values: Readonly<Record<string, string | number>> } {
  if (result === null) return { key: 'evals.evidence.never', values: {} };
  const rate = passRateOf(result);
  if (result.verdict === 'insufficient_evidence') {
    /* `counted` rather than `cases_run`: unavailable cases are excluded from the bar, so a run of
       ten where the model was unreachable eight times counted two — and saying "10 of 10" there
       would claim a measurement that did not happen. */
    return {
      key: 'evals.evidence.thin',
      values: { counted: result.counted, minimum: minimumCases },
    };
  }
  if (rate === null) return { key: 'evals.evidence.noRate', values: {} };
  if (result.verdict === 'failed') {
    /* The BAR, not the achieved rate — the achieved rate is already implied by "N of M correct",
       and the actionable half of a failure is the number it had to beat. An earlier draft passed
       the rate here, which would have rendered "below the 40% bar" for a model that scored 40%. */
    return {
      key: 'evals.evidence.failed',
      values: {
        passed: result.passed,
        counted: result.counted,
        bar: `${(minimumPassRate * 100).toFixed(0)}%`,
      },
    };
  }
  return {
    key: 'evals.evidence.passed',
    values: { passed: result.passed, counted: result.counted },
  };
}

/** How many models the matrix would withhold a feature from. `0` is the healthy answer.
 *
 * Counted from `gates` rather than from the verdict, because `gates` is the field the platform derives
 * for exactly this question — and reading the verdict instead would mean this screen re-implementing
 * the gating rule, which is how a screen and a gate come to disagree about which models are blocked.
 */
export function gatedCount(results: readonly CapabilityResult[]): number {
  return results.filter((result) => result.gates).length;
}

/** The shortfall between the corpus this deployment ships and the one the specification asks for.
 *
 * Returned as a pair rather than a ratio, because the ratio is the misleading form: "6%" invites
 * somebody to read a percentage of a target, while "16 of 700" is the sentence that makes a PROVEN
 * verdict's weight legible. `meets_specification` on the report is the boolean; this is the arithmetic
 * behind it.
 */
export function corpusShortfall(
  capabilities: readonly {
    readonly cases_here: number;
    readonly cases_specified: number;
  }[],
): { readonly here: number; readonly specified: number } {
  return {
    here: capabilities.reduce((sum, entry) => sum + entry.cases_here, 0),
    specified: capabilities.reduce((sum, entry) => sum + entry.cases_specified, 0),
  };
}
